import { FC, useState, useEffect, createContext, useContext, useRef, Dispatch, SetStateAction } from 'react';
import { LayoutSplashScreen } from '../../../../_metronic/layout/core';
import { AuthModel, UserModel } from './_models';
import * as authHelper from './AuthHelpers';
import { WithChildren } from '../../../../_metronic/helpers';
import { Auth } from 'aws-amplify';
import { getUserByCognitoId } from './_requests';
import { FileContext } from 'app/modules/accounts/Filecontext';
import { USER_ROLES } from "../../../../helpers/utils";
import * as Sentry from '@sentry/react';

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<any | undefined>>;
  logout: () => void;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const logout = () => {
    // Clear Sentry user and company context on logout
    Sentry.setUser(null);
    Sentry.setContext('company', null);
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User logged out',
      level: 'info',
    });

    localStorage.clear();
    saveAuth(undefined);
    setCurrentUser(undefined);
  };

  if (currentUser?.isSuperAdmin) {
    const currentRole = window.localStorage.getItem('userRole');
    !currentRole && window.localStorage.setItem('userRole', USER_ROLES.editor);
  }

  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, currentUser, logout, setCurrentUser } = useAuth();
  const didRequest = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const user = await Auth.currentUserPoolUser();
          const { data } = await getUserByCognitoId(auth?.cognitoID as string);
          const admin = { ...user, ...data };
          if (admin) {
            setCurrentUser(admin);

            // Set Sentry user context
            Sentry.setUser({
              id: admin.id?.toString(),
              email: admin.email,
              username: admin.firstname && admin.lastname
                ? `${admin.firstname} ${admin.lastname}`
                : admin.email,
            });

            // Set company context
            if (admin.companyId) {
              Sentry.setContext('company', {
                companyId: admin.companyId,
                isSuperAdmin: admin.isSuperAdmin || false,
              });
            }

            // Add breadcrumb for successful login
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'User logged in',
              level: 'info',
            });
          }
        }
      } catch (error) {
        console.error(error);
        // Add breadcrumb for failed auth
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Failed to get user',
          level: 'error',
        });
      } finally {
        setShowSplashScreen(false);
      }

      return () => (didRequest.current = true);
    };

    if (auth && auth.api_token) {
      requestUser();
    } else {
      logout();
      setShowSplashScreen(false);
    }
    // eslint-disable-next-line
  }, []);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
